import '../styles/DashboardBalanceGauge.css';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useProjectCollection } from '../lib/projectData';

/* ---- Dial geometry (viewBox 0 0 200 200) ---- */
const DIAL_MIN_DEG = -110;
const DIAL_MAX_DEG = 110;
const DIAL_MAX_VALUE = 50000;
const HUB_X = 100;
const HUB_Y = 100;
const NEEDLE_LEN = 62;

/* Fix the rendered frame so loading, unavailable and success renders
   the same dial without the band shifting. Reads the canonical
   client_accounts record when the shared collection resolves. */
const PREVIEW_ACCOUNT = {
  id: 'acct-4471',
  holderName: 'M. Okonkwo',
  currency: 'USD',
  balance: 18420.55,
};

const TICKS = Array.from({ length: 41 }, (_, i) => {
  const major = i % 5 === 0;
  const angle = DIAL_MIN_DEG + ((DIAL_MAX_DEG - DIAL_MIN_DEG) * i) / 40;
  const rad = ((angle - 90) * Math.PI) / 180;
  const outer = 88;
  const inner = major ? 74 : 81;
  return {
    key: i,
    angle,
    major,
    x1: HUB_X + outer * Math.cos(rad),
    y1: HUB_Y + outer * Math.sin(rad),
    x2: HUB_X + inner * Math.cos(rad),
    y2: HUB_Y + inner * Math.sin(rad),
  };
});

function clamp(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function amountToAngle(value) {
  if (!Number.isFinite(value)) return DIAL_MIN_DEG;
  const ratio = clamp(value / DIAL_MAX_VALUE, 0, 1);
  return DIAL_MIN_DEG + ratio * (DIAL_MAX_DEG - DIAL_MIN_DEG);
}

function polar(angle, length) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return { x: HUB_X + length * Math.cos(rad), y: HUB_Y + length * Math.sin(rad) };
}

function formatAmount(value) {
  const safe = Number.isFinite(value) ? value : 0;
  return safe.toLocaleString('en-US', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
}

function formatClock(date) {
  return date.toLocaleTimeString('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    second: '2-digit',
    hour12: false,
  });
}

const REFRESH_ICON = (
  <svg
    width="14"
    height="14"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.8"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
    focusable="false"
  >
    <path d="M20 12a8 8 0 1 1-2.34-5.66" />
    <path d="M20 4v5h-5" />
  </svg>
);

export default function DashboardBalanceGauge() {
  const { data, loading, error, refresh } = useProjectCollection('client_accounts');

  const account = useMemo(() => {
    const list = Array.isArray(data) ? data : [];
    return (
      list.find((item) => item && item.id === PREVIEW_ACCOUNT.id) ||
      list[0] ||
      null
    );
  }, [data]);

  const reduced = usePrefersReducedMotion();
  const identity = {
    id: (account && account.id) || PREVIEW_ACCOUNT.id,
    holderName: (account && account.holderName) || PREVIEW_ACCOUNT.holderName,
    currency: (account && account.currency) || PREVIEW_ACCOUNT.currency,
  };
  const accountBalance = account && Number.isFinite(account.balance)
    ? account.balance
    : null;

  const [refreshedAt, setRefreshedAt] = useState(() => new Date());
  const [needleAngle, setNeedleAngle] = useState(DIAL_MIN_DEG);

  const balanceReady = !loading && !error && account && Number.isFinite(accountBalance);
  const targetBalance = balanceReady ? accountBalance : null;

  const animated = useAnimatedNumber(targetBalance, reduced);
  const angle = amountToAngle(targetBalance === null ? 0 : targetBalance);

  useEffect(() => {
    if (targetBalance === null) {
      setNeedleAngle(DIAL_MIN_DEG);
      return undefined;
    }
    if (reduced) {
      setNeedleAngle(angle);
      return undefined;
    }
    setNeedleAngle(DIAL_MIN_DEG);
    const frame = window.requestAnimationFrame(() => setNeedleAngle(angle));
    return () => window.cancelAnimationFrame(frame);
  }, [targetBalance, reduced, angle]);

  const handleRefresh = useCallback(() => {
    setRefreshedAt(new Date());
    refresh();
  }, [refresh]);

  const handleRetry = useCallback(() => {
    setRefreshedAt(new Date());
    refresh();
  }, [refresh]);

  const statusClass = loading
    ? 'dbg-status is-loading'
    : error
    ? 'dbg-status is-error'
    : 'dbg-status';
  const statusLabel = loading
    ? 'Loading balance'
    : error
    ? 'Balance unavailable'
    : 'Instrument live';

  const arm = polar(angle, NEEDLE_LEN);
  const tail = polar(angle + 180, 16);

  return (
    <section
      className="dbg-root"
      aria-label="Current balance instrument"
      data-state={loading ? 'loading' : error ? 'error' : 'ready'}
    >
      <div className="dbg-panel">
        <div className="dbg-head">
          <p className="dbg-micro">Current Balance</p>
          <span className={statusClass}>
            <span className="dbg-status-dot" aria-hidden="true" />
            {statusLabel}
          </span>
        </div>

        <div className="dbg-dial">
          <svg
            className="dbg-dial-svg"
            viewBox="0 0 200 200"
            role="img"
            aria-label={
              balanceReady
                ? `Balance gauge: ${formatAmount(account.balance)} ${identity.currency}`
                : 'Balance gauge'
            }
          >
            <circle className="dbg-bezel" cx={HUB_X} cy={HUB_Y} r="94" />
            <circle className="dbg-bezel" cx={HUB_X} cy={HUB_Y} r="70" opacity="0.55" />

            <path
              className="dbg-track"
              d={describeArc(HUB_X, HUB_Y, 88, DIAL_MIN_DEG, DIAL_MAX_DEG)}
            />
            <path
              className="dbg-arc-amber"
              d={describeArc(HUB_X, HUB_Y, 88, DIAL_MIN_DEG, 0)}
            />

            {TICKS.map((tick) => (
              <line
                key={tick.key}
                className={tick.major ? 'dbg-tick is-major' : 'dbg-tick'}
                x1={tick.x1}
                y1={tick.y1}
                x2={tick.x2}
                y2={tick.y2}
              />
            ))}

            <g
              className="dbg-needle"
              style={{ '--dbg-angle': `${needleAngle}deg` }}
            >
              <line
                className="dbg-needle-arm"
                x1={HUB_X}
                y1={HUB_Y}
                x2={arm.x}
                y2={arm.y}
              />
              <line
                className="dbg-needle-tail"
                x1={HUB_X}
                y1={HUB_Y}
                x2={tail.x}
                y2={tail.y}
              />
            </g>

            <circle className="dbg-hub-outer" cx={HUB_X} cy={HUB_Y} r="9" />
            <circle className="dbg-hub-inner" cx={HUB_X} cy={HUB_Y} r="3.4" />
          </svg>

          <div className="dbg-readout" aria-hidden="true">
            {loading ? (
              <span className="dbg-amount is-pending">
                <span className="dbg-skeleton" />
              </span>
            ) : (
              <>
                <span className={balanceReady ? 'dbg-amount' : 'dbg-amount is-pending'}>
                  {balanceReady ? formatAmount(animated) : '------'}
                </span>
                <span className="dbg-currency">
                  {identity.currency} · available balance
                </span>
              </>
            )}
          </div>
        </div>

        <p className="dbg-sr" role="status">
          {loading
            ? 'Loading balance'
            : balanceReady
            ? `Balance ${formatAmount(account.balance)} ${identity.currency}`
            : 'Balance unavailable'}
        </p>

        <div className="dbg-strip">
          <div className="dbg-identity">
            <p className="dbg-holder">{identity.holderName}</p>
            <p className="dbg-account">
              Account <span className="dbg-account-value">{identity.id}</span>
            </p>
          </div>

          <div className="dbg-row">
            <span className="dbg-row-label">Last refreshed</span>
            <span className="dbg-row-value">{formatClock(refreshedAt)}</span>
          </div>

          {error ? (
            <div className="dbg-unavailable">
              <p className="dbg-unavailable-head">Gauge unavailable</p>
              <p className="dbg-unavailable-msg">
                Balance data could not be read. The ledger band is unaffected — retry to
                reload this instrument.
              </p>
              <button
                type="button"
                className="dbg-refresh"
                onClick={handleRetry}
                aria-label="Retry balance data load"
              >
                <span className="dbg-refresh-icon" aria-hidden="true">
                  {REFRESH_ICON}
                </span>
                Retry balance
              </button>
            </div>
          ) : (
            <button
              type="button"
              className={
                loading ? 'dbg-refresh is-spinning' : 'dbg-refresh'
              }
              onClick={handleRefresh}
              disabled={loading}
              aria-label="Refresh balance data"
            >
              <span className="dbg-refresh-icon" aria-hidden="true">
                {REFRESH_ICON}
              </span>
              {loading ? 'Reading instrument' : 'Refresh balance'}
            </button>
          )}
        </div>
      </div>
    </section>
  );
}

/* ---- Animated readout: tabular numerals that count to the loaded value ---- */
function useAnimatedNumber(target, reduced) {
  const [value, setValue] = useState(target === null ? 0 : target);
  const frame = useRef(0);

  useEffect(() => {
    if (target === null) {
      setValue(0);
      return undefined;
    }
    if (reduced) {
      setValue(target);
      return undefined;
    }
    const start = performance.now();
    const from = 0;
    const duration = 700;
    const tick = (now) => {
      const progress = Math.min(1, (now - start) / duration);
      const eased = 1 - Math.pow(1 - progress, 3);
      setValue(from + (target - from) * eased);
      if (progress < 1) {
        frame.current = requestAnimationFrame(tick);
      }
    };
    frame.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frame.current);
  }, [target, reduced]);

  return value;
}

function usePrefersReducedMotion() {
  const [reduced, setReduced] = useState(false);
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const query = window.matchMedia('(prefers-reduced-motion: reduce)');
    setReduced(query.matches);
    const onChange = (event) => setReduced(event.matches);
    query.addEventListener('change', onChange);
    return () => query.removeEventListener('change', onChange);
  }, []);
  return reduced;
}

function polarPoint(angle, radius) {
  const rad = ((angle - 90) * Math.PI) / 180;
  return {
    x: HUB_X + radius * Math.cos(rad),
    y: HUB_Y + radius * Math.sin(rad),
  };
}

function describeArc(cx, cy, radius, startAngle, endAngle) {
  const start = polarPoint(startAngle, radius);
  const end = polarPoint(endAngle, radius);
  const large = Math.abs(endAngle - startAngle) > 180 ? 1 : 0;
  const sweep = endAngle > startAngle ? 1 : 0;
  void cx;
  void cy;
  return `M ${start.x.toFixed(2)} ${start.y.toFixed(2)} A ${radius} ${radius} 0 ${large} ${sweep} ${end.x.toFixed(2)} ${end.y.toFixed(2)}`;
}